import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { GridInitialState, GridState } from '@mui/x-data-grid';

const STORAGE_KEY_PREFIX = 'trades-table-state';

interface UseTradesTablePersistenceReturn {
  initialState: GridInitialState | undefined;
  onStateChange: (state: GridState) => void;
  resetState: () => void;
}

const loadStateFromStorage = (
  storageKey: string,
): GridInitialState | undefined => {
  try {
    const savedState = localStorage.getItem(storageKey);
    if (savedState) {
      const parsedState = JSON.parse(savedState) as GridInitialState;
      if (parsedState && typeof parsedState === 'object') {
        return parsedState;
      } else {
        console.warn(
          'Invalid trades table state structure, ignoring saved state',
        );
        try {
          localStorage.removeItem(storageKey);
        } catch (removeError) {
          console.warn(
            'Failed to remove corrupted state from localStorage:',
            removeError,
          );
        }
      }
    }
  } catch (error) {
    console.warn('Failed to load trades table state from localStorage:', error);
    try {
      localStorage.removeItem(storageKey);
    } catch (removeError) {
      console.warn(
        'Failed to remove corrupted state from localStorage:',
        removeError,
      );
    }
  }
  return undefined;
};

export const useTradesTablePersistence = (
  userId: string,
): UseTradesTablePersistenceReturn => {
  const storageKey = userId ? `${STORAGE_KEY_PREFIX}-${userId}` : null;

  const [initialState] = useState<GridInitialState | undefined>(() => {
    if (!storageKey || !userId) {
      return undefined;
    }
    return loadStateFromStorage(storageKey);
  });

  const debouncedSaveRef = useRef<ReturnType<typeof debounce> | null>(null);
  const isInitialMountRef = useRef(true);
  const isResettingRef = useRef(false);

  useEffect(() => {
    if (!storageKey || !userId) {
      return;
    }

    const timer = setTimeout(() => {
      isInitialMountRef.current = false;
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [storageKey, userId]);

  const saveState = useCallback(
    (state: GridState) => {
      if (!storageKey || !userId) {
        return;
      }

      if (isInitialMountRef.current || isResettingRef.current) {
        return;
      }

      try {
        const stateToSave: GridInitialState = {
          columns: state.columns
            ? {
                columnVisibilityModel: state.columns.columnVisibilityModel,
                orderedFields: state.columns.orderedFields,
              }
            : undefined,
          sorting: state.sorting
            ? {
                sortModel: state.sorting.sortModel,
              }
            : undefined,
          filter: state.filter
            ? {
                filterModel: state.filter.filterModel,
              }
            : undefined,
          density: state.density,
        };

        const serializedState = JSON.stringify(stateToSave);
        localStorage.setItem(storageKey, serializedState);
      } catch (error) {
        if (
          error instanceof DOMException &&
          error.name === 'QuotaExceededError'
        ) {
          console.warn(
            'localStorage quota exceeded, cannot save trades table state',
          );
        } else if (error instanceof TypeError) {
          console.warn('Failed to serialize trades table state:', error);
        } else {
          console.warn(
            'Failed to save trades table state to localStorage:',
            error,
          );
        }
      }
    },
    [storageKey, userId],
  );

  useEffect(() => {
    debouncedSaveRef.current = debounce(saveState, 300);

    return () => {
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current.cancel();
      }
    };
  }, [saveState]);

  const handleStateChange = useCallback((state: GridState) => {
    if (debouncedSaveRef.current) {
      debouncedSaveRef.current(state);
    }
  }, []);

  const resetState = useCallback(() => {
    if (!storageKey || !userId) {
      console.warn(
        'Failed to reset trades table state from localStorage: No storage key or user ID',
      );
      return;
    }

    isResettingRef.current = true;

    try {
      localStorage.removeItem(storageKey);
      setTimeout(() => {
        isResettingRef.current = false;
      }, 2000);
    } catch (error) {
      console.warn(
        'Failed to reset trades table state from localStorage:',
        error,
      );
      isResettingRef.current = false;
    }
  }, [storageKey, userId]);

  return {
    initialState,
    onStateChange: handleStateChange,
    resetState,
  };
};
